import { useState, useRef, useEffect } from 'react'
import { Modal, App, Progress, Typography } from 'antd'
import { CrownFilled } from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useMindmaps } from '../../hooks/useMindmaps'
import { ProfileService } from '../../services/profile/profileService'
import { analytics } from '../../utils/analytics'
import { getInitialNodes, INITIAL_EDGES } from '../../constants/mindmapConfig'
import { StyledInput } from './styles/CreateProjectModalStyles'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const { Text } = Typography

const QuotaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`

const UpgradeLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  cursor: pointer;
  color: #764ba2;

  .anticon {
    font-size: ${TYPOGRAPHY.caption.fontSize};
    color: #667eea;
  }

  &:hover {
    opacity: 0.8;
  }
`

interface ProjectQuota {
  currentCount: number
  projectLimit: number
  canCreate: boolean
  isUnlimited: boolean
}

interface CreateProjectModalProps {
  open: boolean
  onClose: () => void
  onUpgradeClick?: () => void
}

export function CreateProjectModal({ open, onClose, onUpgradeClick }: CreateProjectModalProps) {
  const navigate = useNavigate()
  const { message: messageApi } = App.useApp()
  const { createMindmap } = useMindmaps()

  const [title, setTitle] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [quota, setQuota] = useState<ProjectQuota | null>(null)
  const [loadingQuota, setLoadingQuota] = useState(false)
  const isSubmittingRef = useRef(false)

  // Fetch project quota when modal opens (for progress display)
  useEffect(() => {
    if (open) {
      const fetchQuota = async () => {
        setLoadingQuota(true)
        const { data, error } = await ProfileService.getProjectQuota()
        if (!error && data) {
          setQuota(data)
        }
        setLoadingQuota(false)
      }
      void fetchQuota()
    }
  }, [open])

  const handleCreate = async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) return

    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsSubmitting(true)

    try {
      const { data: mindmapData, error: mindmapError } = await createMindmap({
        title: trimmedTitle,
        content: {
          nodes: getInitialNodes(),
          edges: [...INITIAL_EDGES],
        },
        is_public: false,
      })

      if (mindmapError || !mindmapData) {
        messageApi.error(mindmapError?.message ?? 'Failed to create project')
        return
      }

      messageApi.success('Project created')
      analytics.projectCreated({ title: trimmedTitle })
      handleClose()
      void navigate(`/mindmap/${mindmapData.id}`)
    } catch (err) {
      messageApi.error(err instanceof Error ? err.message : 'Failed to create project')
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setTitle('')
    setQuota(null)
    onClose()
  }

  const quotaPercent =
    quota && !quota.isUnlimited
      ? Math.min(100, Math.round((quota.currentCount / quota.projectLimit) * 100))
      : 0

  return (
    <Modal
      title="Create New Project"
      open={open}
      onOk={handleCreate}
      onCancel={handleClose}
      okText="Create"
      okButtonProps={{
        loading: isSubmitting,
        disabled: !title.trim() || isSubmitting || loadingQuota,
      }}
      cancelButtonProps={{ disabled: isSubmitting }}
      destroyOnHidden
    >
      {quota && !quota.isUnlimited && (
        <div style={{ marginBottom: 16 }}>
          <QuotaRow>
            <Text type="secondary" style={{ fontSize: TYPOGRAPHY.caption.fontSize }}>
              Projects: {quota.currentCount} / {quota.projectLimit}
            </Text>
            {onUpgradeClick && (
              <UpgradeLink
                onClick={() => {
                  onClose()
                  onUpgradeClick()
                }}
              >
                <CrownFilled /> Upgrade for unlimited
              </UpgradeLink>
            )}
          </QuotaRow>
          <Progress
            percent={quotaPercent}
            size="small"
            status={quotaPercent >= 80 ? 'active' : 'normal'}
            showInfo={false}
          />
        </div>
      )}

      <StyledInput
        placeholder="Project title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onPressEnter={() => void handleCreate()}
        autoFocus
        maxLength={100}
        disabled={isSubmitting}
      />
    </Modal>
  )
}
